import { useState } from "react";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MessageCircle,
  Rss,
  Send,
  ShoppingBag,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { OffersFeed } from "@/components/feed/OffersFeed";
import { marketplaceLabel } from "@/lib/marketplace";
import { useFeed } from "@/lib/hooks/useFeed";
import type {
  FeedConversionPayload,
  FeedEvent,
  FeedPostSentPayload,
} from "@/lib/types";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `há ${diff}s`;
  if (diff < 3600) return `há ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  if (diff < 2592000) return `há ${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("pt-BR");
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const CHANNEL_LABEL: Record<FeedPostSentPayload["channel"], string> = {
  telegram: "Telegram",
  whatsapp: "WhatsApp",
};

// ---------------------------------------------------------------------------
// Estados de carregamento / vazio / erro
// ---------------------------------------------------------------------------

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Carregando feed">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="h-24 w-full animate-pulse rounded-xl border border-border bg-[hsl(var(--surface-raised)/0.4)]"
        />
      ))}
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] py-16 text-center">
      <Rss size={40} className="text-muted-foreground/40" aria-hidden="true" />
      <p className="text-sm font-semibold text-foreground">
        Nenhuma atividade ainda
      </p>
      <p className="max-w-md text-sm text-muted-foreground leading-relaxed">
        Quando um link gerar uma conversão ou um post agendado for enviado,
        o registro aparece aqui em ordem cronológica.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badge de contagem (header) e controles de paginação, extraídos para manter
// a complexidade do componente principal sob controle.
// ---------------------------------------------------------------------------

function FeedCountBadge({ total, isFetching }: { total: number; isFetching: boolean }): JSX.Element {
  return (
    <div className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border bg-[hsl(var(--surface-raised))] px-3 py-1.5 text-xs font-medium text-muted-foreground">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          isFetching ? "bg-[hsl(var(--gold))] animate-pulse" : "bg-primary",
        )}
      />
      {total} {total === 1 ? "evento" : "eventos"}
    </div>
  );
}

function FeedPagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 pt-1">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--border-strong))]",
          "bg-[hsl(var(--surface-raised))] px-3 py-1.5 text-xs font-medium text-foreground",
          "transition-colors hover:bg-[hsl(var(--surface-raised)/0.7)] hover:border-primary/30",
          "disabled:cursor-not-allowed disabled:opacity-40",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        <ChevronLeft size={13} />
        Anterior
      </button>

      <span className="text-xs text-muted-foreground tabular-nums font-mono">
        página {page} de {totalPages}
      </span>

      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--border-strong))]",
          "bg-[hsl(var(--surface-raised))] px-3 py-1.5 text-xs font-medium text-foreground",
          "transition-colors hover:bg-[hsl(var(--surface-raised)/0.7)] hover:border-primary/30",
          "disabled:cursor-not-allowed disabled:opacity-40",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        Próxima
        <ChevronRight size={13} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Derivacao pura do que exibir, isolada do componente para nao inflar sua
// complexidade ciclomatica (regra max-complexity 20 do eslint da casa).
// ---------------------------------------------------------------------------

interface FeedViewState {
  showCountBadge: boolean;
  showEmptyState: boolean;
  showEvents: boolean;
  showPagination: boolean;
}

function computeFeedViewState(
  isLoading: boolean,
  hasError: boolean,
  eventsLength: number,
  total: number,
  totalPages: number,
): FeedViewState {
  const settled = !isLoading && !hasError;
  return {
    showCountBadge: settled && total > 0,
    showEmptyState: settled && eventsLength === 0,
    showEvents: settled && eventsLength > 0,
    showPagination: settled && totalPages > 1,
  };
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

type FeedTab = "ofertas" | "atividade";

const FEED_TABS: ReadonlyArray<{ id: FeedTab; label: string }> = [
  { id: "ofertas", label: "Ofertas" },
  { id: "atividade", label: "Atividade" },
];

/** Feed de promocoes: ofertas das lojas (padrao) e atividade da conta. */
export function FeedPage(): JSX.Element {
  const [tab, setTab] = useState<FeedTab>("ofertas");
  return (
    <div className="max-w-6xl space-y-6">
      <PageHeader
        title="Feed de promoções"
        subtitle="Ofertas das lojas com link de afiliado na hora, e a atividade recente da sua conta."
      />
      <div className="flex gap-1 border-b border-border pb-px" role="tablist" aria-label="Seções do feed">
        {FEED_TABS.map((item) => (
          <button
            key={item.id}
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={cn(
              "relative px-4 py-2.5 text-sm font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              tab === item.id ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
            {tab === item.id && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-primary" aria-hidden="true" />}
          </button>
        ))}
      </div>
      <div role="tabpanel">{tab === "ofertas" ? <OffersFeed /> : <ActivityFeed />}</div>
    </div>
  );
}

/** Atividade da conta (conversoes e posts enviados), da view feed_events. */
export function ActivityFeed(): JSX.Element {
  const [page, setPage] = useState(1);

  const { data: result, isLoading, error, isFetching } = useFeed(page, PAGE_SIZE);

  const events = result?.data ?? [];
  const total = result?.total ?? 0;
  const totalPages = result?.totalPages ?? 1;
  const { showCountBadge, showEmptyState, showEvents, showPagination } = computeFeedViewState(
    isLoading,
    Boolean(error),
    events.length,
    total,
    totalPages,
  );

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Conversões recentes e posts enviados aos seus grupos e canais, em ordem cronológica.
        </p>
        {showCountBadge && <FeedCountBadge total={total} isFetching={isFetching} />}
      </div>

      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle size={16} className="shrink-0" />
          <span>Erro ao carregar o feed: {error.message}</span>
        </div>
      )}

      {isLoading && <LoadingSkeleton />}

      {showEmptyState && <EmptyState />}

      {showEvents && (
        <div className={cn("space-y-4 transition-opacity", isFetching && "opacity-75")}>
          {events.map((event) => (
            <FeedCard key={event.id} event={event} />
          ))}
        </div>
      )}

      {showPagination && (
        <FeedPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card individual do feed - layout varia por event_type
// ---------------------------------------------------------------------------

interface FeedCardProps {
  event: FeedEvent;
}

function FeedCard({ event }: FeedCardProps): JSX.Element {
  if (event.event_type === "post_sent") {
    return <PostSentCard event={event} payload={event.payload as FeedPostSentPayload} />;
  }
  return <ConversionCard event={event} payload={event.payload as FeedConversionPayload} />;
}

function ConversionCard({
  event,
  payload,
}: {
  event: FeedEvent;
  payload: FeedConversionPayload;
}): JSX.Element {
  const label = payload.marketplaceCode ? marketplaceLabel(payload.marketplaceCode) : "Marketplace";

  return (
    <Card className="overflow-hidden transition-shadow duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)] hover:shadow-md">
      <CardContent className="flex gap-4 p-4 sm:gap-5 sm:p-5">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <ShoppingBag size={18} className="text-primary" aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold leading-snug text-foreground">
              Conversão gerada
            </h3>
            <Badge variant="muted" className="gap-1">
              {label}
            </Badge>
          </div>

          {payload.originalUrl && (
            <div className="flex items-center gap-2">
              <code className="max-w-[280px] truncate rounded bg-[hsl(var(--surface-raised))] px-2 py-1 font-mono text-[11px] text-muted-foreground">
                {payload.originalUrl}
              </code>
              <a
                href={payload.originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80"
              >
                Abrir
                <ExternalLink size={11} aria-hidden="true" />
              </a>
            </div>
          )}

          <p className="text-xs text-muted-foreground" title={formatDate(event.occurred_at)}>
            {timeAgo(event.occurred_at)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function PostSentCard({
  event,
  payload,
}: {
  event: FeedEvent;
  payload: FeedPostSentPayload;
}): JSX.Element {
  const ChannelIcon = payload.channel === "whatsapp" ? MessageCircle : Send;

  return (
    <Card className="overflow-hidden transition-shadow duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)] hover:shadow-md">
      <CardContent className="flex gap-4 p-4 sm:gap-5 sm:p-5">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/10">
          <ChannelIcon size={18} className="text-gold" aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold leading-snug text-foreground">
              Post enviado
            </h3>
            <Badge variant="muted" className="gap-1">
              {CHANNEL_LABEL[payload.channel]}
            </Badge>
          </div>

          <p className="truncate text-xs text-muted-foreground">
            Destino: <span className="font-mono">{payload.targetId}</span>
          </p>

          <p className="text-xs text-muted-foreground" title={formatDate(event.occurred_at)}>
            {timeAgo(event.occurred_at)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
